import { Injectable, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { catchError, map, Observable, of } from 'rxjs';

import { ApiClientService } from '../../../core/http/api-client.service';
import {
  ClientProfile,
  ClientProfileDto,
  UpdateMyClientProfileRequest
} from './client-profile.models';

@Injectable({
  providedIn: 'root'
})
export class ClientProfileService
{
  private readonly apiClient = inject(ApiClientService);

  public getMyProfile(): Observable<ClientProfile | null>
  {
    return this.apiClient.get<ClientProfileDto>('/clients/me').pipe(
      catchError((error: unknown) => {
        if (error instanceof HttpErrorResponse && error.status === 404) {
          return of(null);
        }

        throw error;
      }),
      map((client) => {
        if (!client) {
          return null;
        }

        return {
          id: client.id,
          userId: client.userId,
          photoUrl: client.photoUrl ?? null,
          fullName: client.fullName,
          phone: client.phone,
          email: client.email,
          birthDate: client.birthDate,
          accountOrigin: client.accountOrigin,
          accountOriginSetAt: client.accountOriginSetAt,
          accountStatus: client.accountStatus,
          inviteExpiresAt: client.inviteExpiresAt,
          createdAt: client.createdAt,
          updatedAt: client.updatedAt,
        };
      })
    );
  }

  public updateMyProfile(payload: UpdateMyClientProfileRequest): Observable<ClientProfile>
  {
    return this.apiClient.patch<ClientProfileDto, UpdateMyClientProfileRequest>(
      '/clients/me',
      payload
    ).pipe(
      map((dto) => ({
        id: dto.id,
        userId: dto.userId,
        photoUrl: dto.photoUrl ?? null,
        fullName: dto.fullName,
        phone: dto.phone,
        email: dto.email,
        birthDate: dto.birthDate,
        accountOrigin: dto.accountOrigin,
        accountOriginSetAt: dto.accountOriginSetAt,
        accountStatus: dto.accountStatus,
        inviteExpiresAt: dto.inviteExpiresAt,
        createdAt: dto.createdAt,
        updatedAt: dto.updatedAt,
      }))
    );
  }

  public uploadMyPhoto(file: File): Observable<ClientProfile> {
    const body = new FormData();
    body.append('file', file);

    return this.apiClient.post<ClientProfileDto, FormData>(
      '/clients/me/photo',
      body,
    ).pipe(
      map((dto) => ({
        id: dto.id,
        userId: dto.userId,
        photoUrl: dto.photoUrl ?? null,
        fullName: dto.fullName,
        phone: dto.phone,
        email: dto.email,
        birthDate: dto.birthDate,
        accountOrigin: dto.accountOrigin,
        accountOriginSetAt: dto.accountOriginSetAt,
        accountStatus: dto.accountStatus,
        inviteExpiresAt: dto.inviteExpiresAt,
        createdAt: dto.createdAt,
        updatedAt: dto.updatedAt,
      }))
    );
  }

  public removeMyPhoto(): Observable<ClientProfile> {
    return this.apiClient.patch<ClientProfileDto, Record<string, never>>(
      '/clients/me/photo/remove',
      {},
    ).pipe(
      map((dto) => ({
        id: dto.id,
        userId: dto.userId,
        photoUrl: dto.photoUrl ?? null,
        fullName: dto.fullName,
        phone: dto.phone,
        email: dto.email,
        birthDate: dto.birthDate,
        accountOrigin: dto.accountOrigin,
        accountOriginSetAt: dto.accountOriginSetAt,
        accountStatus: dto.accountStatus,
        inviteExpiresAt: dto.inviteExpiresAt,
        createdAt: dto.createdAt,
        updatedAt: dto.updatedAt,
      }))
    );
  }
}
